// src/services/userService.ts

import { Cookies } from "react-cookie";

export type MeDto = {
  id?: string | null;
  username: string;
  role?: string;
  displayName?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  photoURL?: string | null;
  provider?: "google" | "apple" | "password";
  country?: string | null;
};

export type SignUpPayload = {
  email: string;
  password: string;
  username: string;
  firstname: string;
  lastname: string;
  street: string;
  number: string;
  addition?: string;
  zip: string;
  city: string;
  country: string;
  picture: string;
  bio: string;
  geo_hide: boolean;
  language: string;
  iban: string;
  bic: string;
};

export type SignInPayload = {
  email: string;
  password: string;
};

// getAllShirts(): Promise<Shirt[]> {
//   var access_token_cookie = getCookie("access_token");
//   if (!access_token_cookie) throw new Error("No access token found");
// const res = await fetch(`${API_BASE}/shirts`, {
//   method: "GET",
//   headers: { "Content-Type": "application/json", "Authorization": `Bearer ${access_token_cookie.value}` },
//   credentials: "include",
// });

const API_BASE =
  process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "http://localhost:8080";

const cookies = new Cookies();

type AuthApiResponse = {
  user?: {
    id?: string | number | null;
    username?: string;
    displayName?: string | null;
    email?: string | null;
    firstname?: string | null;
    lastname?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    photoURL?: string | null;
    picture?: string | null;
    role?: string;
    country?: string | null;
  };
  access_token?: string;
  refresh_token?: string;
};

type SignUpApiResponse = {
  access_token: string;
  refresh_token: string;
  token_type: "bearer" | string;
  user?: AuthApiResponse["user"];
};

async function parseError(res: Response) {
  try {
    const data = await res.json();
    if (Array.isArray(data?.detail)) {
      const details = data.detail
        .map((d: { loc?: Array<string | number>; msg?: string }) => {
          const loc = Array.isArray(d?.loc) ? d.loc.join(".") : "";
          return loc ? `${loc}: ${d?.msg || "Validatiefout"}` : d?.msg || "Validatiefout";
        })
        .join("; ");
      if (details) return details;
    }
    if (typeof data?.detail === "string") return data.detail;
    return data?.message || data?.error || "Er ging iets mis";
  } catch {
    return "Er ging iets mis";
  }
}

async function parseJsonSafe<T>(res: Response): Promise<T | null> {
  try {
    const text = await res.text();
    if (!text) return null;
    return JSON.parse(text) as T;
  } catch {
    return null;
  }
}

function getAccessTokenFromCookies(): string | null {
  const token = cookies.get("access_token");
  if (typeof token !== "string") return null;

  const normalizedToken = token.trim();
  return normalizedToken ? normalizedToken : null;
}

function clearAuthCookies() {
  cookies.remove("access_token", { path: "/" });
  cookies.remove("refresh_token", { path: "/" });
}

function mapAuthResponseToMe(data: AuthApiResponse | null, fallbackId: string): MeDto {
  const user = data?.user;
  const email = user?.email ?? null;
  const firstName = user?.firstname ?? user?.first_name ?? null;
  const lastName = user?.lastname ?? user?.last_name ?? null;
  const combinedName = `${firstName ?? ""} ${lastName ?? ""}`.trim();
  const displayName =
    user?.displayName ??
    (combinedName || null) ??
    email?.split("@")[0] ??
    fallbackId;

  const rawUsername = user?.username ?? email ?? fallbackId;
  const username =
    rawUsername.includes("@") && displayName ? displayName : rawUsername;

  return {
    id: user?.id != null ? String(user.id) : null,
    username,
    displayName,
    email,
    firstName,
    lastName,
    photoURL: user?.photoURL ?? user?.picture ?? null,
    role: user?.role ?? "USER",
    provider: "password",
    country: user?.country ?? null,
  };
}

// TODO: add httponly cookie handling in backend and add secure flags to cookies here (sameSite, secure)
async function apiMe(): Promise<MeDto> {
  const accessToken = getAccessTokenFromCookies();
  if (!accessToken) throw new Error("Niet ingelogd");

  const res = await fetch(`${API_BASE}/auth/user`, {
    method: "GET",
    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${accessToken}` },
    credentials: "include",
  });

  if (!res.ok) throw new Error("Niet ingelogd");
  const data = await parseJsonSafe<Record<string, any>>(res);

  // GET /auth/user returns the user object directly (not nested under "user")
  // Wrap it so mapAuthResponseToMe can process it correctly
  const wrapped: AuthApiResponse = data?.user ? data as AuthApiResponse : { user: data as AuthApiResponse["user"] };
  return mapAuthResponseToMe(wrapped, "user");
}

export type MyActivity = {
  has_bids: boolean;
  has_listings: boolean;
  has_purchases?: boolean;
  has_approved_listings?: boolean;
};

async function apiMyActivity(): Promise<MyActivity> {
  const accessToken = getAccessTokenFromCookies();
  if (!accessToken) throw new Error("Niet ingelogd");

  const res = await fetch(`${API_BASE}/users/me/activity`, {
    method: "GET",
    headers: { Authorization: `Bearer ${accessToken}` },
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Activiteit ophalen mislukt (${res.status})`);
  return res.json();
}

async function apiSaveGaClientId(clientId: string): Promise<void> {
  const accessToken = getAccessTokenFromCookies();
  if (!accessToken) throw new Error("Niet ingelogd");

  const res = await fetch(`${API_BASE}/users/me/ga-client-id`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    credentials: "include",
    body: JSON.stringify({ client_id: clientId }),
  });
  if (!res.ok) throw new Error(`client_id opslaan mislukt (${res.status})`);
}

async function apiSignUp(payload: SignUpPayload): Promise<MeDto> {
  const res = await fetch(`${API_BASE}/auth/signup`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(payload)
  });

  if (!res.ok) throw new Error(await parseError(res));

  const data = await parseJsonSafe<SignUpApiResponse>(res);

  // Store tokens from signup response
  if (data?.access_token) {
    cookies.set("access_token", data.access_token, { path: "/", sameSite: "lax", secure: window.location.protocol === "https:" });
  }
  if (data?.refresh_token) {
    cookies.set("refresh_token", data.refresh_token, { path: "/", sameSite: "lax", secure: window.location.protocol === "https:" });
  }

  const fallbackName =
    `${payload.firstname} ${payload.lastname}`.trim() ||
    payload.username?.trim() ||
    payload.email?.split("@")[0] ||
    "user";

  const mappedPayload: AuthApiResponse | null = data?.user ? { user: data.user } : null;
  return mapAuthResponseToMe(mappedPayload, fallbackName);
}

async function apiSignIn(payload: SignInPayload): Promise<MeDto> {
  const res = await fetch(`${API_BASE}/auth/signin`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(payload),
  });

  if (!res.ok) throw new Error(await parseError(res));

  const data = await parseJsonSafe<AuthApiResponse>(res);

  if (data?.access_token) {
    cookies.set("access_token", data.access_token, { path: "/", sameSite: "lax", secure: window.location.protocol === "https:" });
  }
  if (data?.refresh_token) {
    cookies.set("refresh_token", data.refresh_token, { path: "/", sameSite: "lax", secure: window.location.protocol === "https:" });
  }

  return mapAuthResponseToMe(data, payload.email?.split("@")[0] || "user");
}

async function apiSignOut(): Promise<void> {
  try {
    const refreshToken = cookies.get("refresh_token") || "";
    const res = await fetch(`${API_BASE}/auth/signout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    // If backend already considers the session invalid, treat logout as completed.
    if (res.status === 401 || res.status === 403) {
      return;
    }

    if (!res.ok) throw new Error(await parseError(res));
  } finally {
    clearAuthCookies();
  }
}

async function apiGoogleLogin(idToken: string): Promise<MeDto> {
  return apiOAuthLoginReal("google", idToken);
}

async function apiOAuthLoginReal(provider: string, token: string): Promise<MeDto> {
  const res = await fetch(`${API_BASE}/auth/signin/oauth/${provider}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ token }),
  });

  if (!res.ok) throw new Error(await parseError(res));

  const data = await parseJsonSafe<AuthApiResponse & { access_token?: string; refresh_token?: string }>(res);

  if (data?.access_token) {
    cookies.set("access_token", data.access_token, { path: "/", sameSite: "lax", secure: window.location.protocol === "https:" });
  }
  if (data?.refresh_token) {
    cookies.set("refresh_token", data.refresh_token, { path: "/", sameSite: "lax", secure: window.location.protocol === "https:" });
  }

  const me = mapAuthResponseToMe(data, provider);
  return { ...me, provider: provider as MeDto["provider"] };
}

export type OAuthProviderName = "google" | "apple";

export type OAuthLoginPayload = {
  idToken: string;
  provider: OAuthProviderName;
};

async function apiOAuthLogin(payload: OAuthLoginPayload): Promise<MeDto> {
  return apiOAuthLoginReal(payload.provider, payload.idToken);
}

/* ----------------------------- PROFILE UPDATE ----------------------------- */

export type ProfileUpdatePayload = {
  firstname?: string | null;
  lastname?: string | null;
  username?: string | null;
  email?: string | null;
  password?: string | null;
  street?: string | null;
  number?: string | null;
  addition?: string | null;
  box?: string | null;
  zip?: string | null;
  city?: string | null;
  country?: string | null;
  picture?: string | null;
  bio?: string | null;
  geo_hide?: boolean | null;
  language?: string | null;
  iban?: string | null;
  bic?: string | null;
};

/** Fetch the full user profile (including address fields) from GET /auth/user */
async function apiFetchProfile(): Promise<Record<string, any>> {
  const accessToken = getAccessTokenFromCookies();
  if (!accessToken) throw new Error("Niet ingelogd");

  const res = await fetch(`${API_BASE}/auth/user`, {
    method: "GET",
    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${accessToken}` },
    credentials: "include",
  });

  if (!res.ok) throw new Error("Niet ingelogd");
  const data = await parseJsonSafe<Record<string, any>>(res);
  // GET /auth/user returns the user object directly or nested under "user"
  return data?.user ?? data ?? {};
}

async function apiUpdateProfile(payload: ProfileUpdatePayload): Promise<Record<string, any>> {
  const accessToken = getAccessTokenFromCookies();
  if (!accessToken) throw new Error("Niet ingelogd");

  const res = await fetch(`${API_BASE}/auth/profile`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${accessToken}`,
    },
    credentials: "include",
    body: JSON.stringify(payload),
  });

  if (!res.ok) throw new Error(await parseError(res));
  return (await parseJsonSafe<Record<string, any>>(res)) ?? {};
}

/* ----------------------------- PUBLIC PROFILE ----------------------------- */

export type PublicProfile = {
  username: string;
  displayName: string | null;
  bio: string | null;
  photoURL: string | null;
};

async function apiGetPublicProfile(username: string): Promise<PublicProfile> {
  const res = await fetch(`${API_BASE}/auth/user/${encodeURIComponent(username)}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  if (!res.ok) throw new Error("Gebruiker niet gevonden");
  const data = await parseJsonSafe<Record<string, any>>(res);
  const u = data?.user ?? data ?? {};

  return {
    username: u.username ?? username,
    displayName: u.displayName ?? u.firstname
      ? `${u.firstname ?? ""} ${u.lastname ?? ""}`.trim() || null
      : null,
    bio: u.bio ?? null,
    photoURL: u.photoURL ?? u.picture ?? null,
  };
}

/* ------------------------ SELLER PROFILE BY USER ID ----------------------- */

export type SellerProfile = {
  id: string;
  display_name: string;
  picture: string | null;
  bio: string | null;
  city: string | null;
  country: string | null;
  rating: number;
  review_count: number;
  total_completed_sales: number;
  member_since: string;
};

export type SellerReviewer = {
  id: string;
  display_name: string;
  picture: string | null;
};

export type SellerReview = {
  id: string;
  rating: number;
  comment: string;
  item_as_described: boolean;
  shipping_speed: boolean;
  communication: boolean;
  created_at: string;
  reviewer: SellerReviewer;
};

async function apiGetSellerProfile(userId: string): Promise<SellerProfile> {
  const res = await fetch(`${API_BASE}/users/${encodeURIComponent(userId)}/profile`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error("Verkoper niet gevonden");
  const data = (await parseJsonSafe<SellerProfile>(res)) ?? ({} as SellerProfile);
  return data;
}

async function apiGetSellerShirts(userId: string): Promise<any[]> {
  const res = await fetch(`${API_BASE}/users/${encodeURIComponent(userId)}/shirts`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error("Shirts ophalen mislukt");
  return (await parseJsonSafe<any[]>(res)) ?? [];
}

async function apiGetSellerReviews(userId: string): Promise<SellerReview[]> {
  const res = await fetch(`${API_BASE}/users/${encodeURIComponent(userId)}/reviews`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error("Reviews ophalen mislukt");
  return (await parseJsonSafe<SellerReview[]>(res)) ?? [];
}

/* ----------------------------- PUBLIC SERVICE ----------------------------- */

export const userService = {
  async signUp(payload: SignUpPayload): Promise<MeDto> {
    return apiSignUp(payload);
  },

  async signIn(payload: SignInPayload): Promise<MeDto> {
    return apiSignIn(payload);
  },

  async signOut(): Promise<void> {
    return apiSignOut();
  },

  async login(username: string, password: string): Promise<MeDto> {
    return apiSignIn({ email: username, password });
  },

  async me(): Promise<MeDto> {
    return apiMe();
  },

  async myActivity(): Promise<MyActivity> {
    return apiMyActivity();
  },

  async saveGaClientId(clientId: string): Promise<void> {
    return apiSaveGaClientId(clientId);
  },

  async logout(): Promise<void> {
    return apiSignOut();
  },

  async googleLogin(idToken: string): Promise<MeDto> {
    return apiGoogleLogin(idToken);
  },

  async oauthLogin(payload: OAuthLoginPayload): Promise<MeDto> {
    return apiOAuthLogin(payload);
  },

  async updateProfile(payload: ProfileUpdatePayload): Promise<Record<string, any>> {
    return apiUpdateProfile(payload);
  },

  async fetchProfile(): Promise<Record<string, any>> {
    return apiFetchProfile();
  },

  async getPublicProfile(username: string): Promise<PublicProfile> {
    return apiGetPublicProfile(username);
  },

  async getSellerProfile(userId: string): Promise<SellerProfile> {
    return apiGetSellerProfile(userId);
  },

  async getSellerShirts(userId: string): Promise<any[]> {
    return apiGetSellerShirts(userId);
  },

  async getSellerReviews(userId: string): Promise<SellerReview[]> {
    return apiGetSellerReviews(userId);
  },
};
